"use client";

import { useState, useRef, useEffect, type FormEvent } from "react";
import { MessageCircle, X, Send, Bot } from "lucide-react";
import { trackEvent } from "@/lib/analyticsClient";

type Msg = { role: "user" | "assistant"; content: string };

const GREETING: Msg = {
  role: "assistant",
  content: "Сайн байна уу! Би agenticforce-ийн туслах. Дижитал ажилтнуудын тухай асуугаарай 👋",
};

interface LeadForm {
  name: string;
  phone: string;
  email: string;
  company: string;
  consent: boolean;
}

const EMPTY_LEAD_FORM: LeadForm = { name: "", phone: "", email: "", company: "", consent: false };

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [leadStage, setLeadStage] = useState<"form" | "chat">("form");
  const [leadForm, setLeadForm] = useState<LeadForm>(EMPTY_LEAD_FORM);
  const [leadErrors, setLeadErrors] = useState<{ name?: string; contact?: string; consent?: string }>({});
  const [messages, setMessages] = useState<Msg[]>([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  function updateLeadField<K extends keyof LeadForm>(key: K, value: LeadForm[K]) {
    setLeadForm((f) => ({ ...f, [key]: value }));
  }

  function submitLead(e: FormEvent) {
    e.preventDefault();
    const errors: typeof leadErrors = {};
    if (!leadForm.name.trim()) errors.name = "Нэрээ оруулна уу";
    if (!leadForm.phone.trim() && !leadForm.email.trim()) errors.contact = "Утас эсвэл имэйлийн аль нэгийг оруулна уу";
    if (!leadForm.consent) errors.consent = "Зөвшөөрлийг баталгаажуулна уу";
    setLeadErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // Best-effort — a CRM hiccup should never block the chat itself.
    fetch("/api/leads", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...leadForm, source: "chat" }),
    }).catch(() => {});
    trackEvent("lead_submit", { source: "chat" });

    setLeadStage("chat");
  }

  async function send() {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user" as const, content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    trackEvent("chat_message_sent");
    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: next.filter((m) => m !== GREETING) }),
      });
      const data = await res.json();
      const reply =
        res.ok && data.reply
          ? data.reply
          : "Уучлаарай, одоогоор холбогдож чадсангүй. Дахин оролдоно уу.";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Сүлжээний алдаа гарлаа — дахин оролдоно уу." },
      ]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((o) => {
          if (!o) trackEvent("chat_open");
          return !o;
        })}
        aria-label="AI туслахыг нээх"
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--af-accent)] text-black shadow-lg transition hover:bg-[var(--af-accent-hover)]"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-5 z-50 flex h-[70vh] max-h-[520px] w-[min(92vw,380px)] flex-col overflow-hidden rounded-2xl border border-[var(--af-border)] bg-[var(--af-panel)] shadow-2xl">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-[var(--af-border)] bg-[var(--af-bg-soft)] px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[var(--af-accent)] text-black">
              <Bot size={18} strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-sm font-semibold text-[var(--af-text)]">agenticforce AI</div>
              <div className="flex items-center gap-1.5 text-xs text-[var(--af-muted)]">
                <span className="pixel-dot pixel-dot--green" /> Онлайн
              </div>
            </div>
          </div>

          {leadStage === "form" ? (
            <form onSubmit={submitLead} className="flex-1 overflow-y-auto px-4 py-4">
              <p className="text-sm text-[var(--af-muted)]">
                Чатыг эхлүүлэхийн өмнө холбогдох мэдээллээ бөглөнө үү.
              </p>

              <div className="mt-4 space-y-3">
                <div>
                  <label htmlFor="cw-name" className="mb-1 block text-[12px] font-medium text-[var(--af-text)]">
                    Нэр
                  </label>
                  <input
                    id="cw-name"
                    type="text"
                    value={leadForm.name}
                    onChange={(e) => updateLeadField("name", e.target.value)}
                    placeholder="Нэрээ оруулна уу"
                    aria-invalid={!!leadErrors.name}
                    className="w-full rounded-lg border border-[var(--af-border)] bg-[var(--af-bg)] px-3 py-2 text-sm text-[var(--af-text)] outline-none placeholder:text-[var(--af-muted)] focus:border-[var(--af-accent)]"
                  />
                  {leadErrors.name && <p className="mt-1 text-[11px] text-[#ef4444]">{leadErrors.name}</p>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="cw-phone" className="mb-1 block text-[12px] font-medium text-[var(--af-text)]">
                      Утас
                    </label>
                    <input
                      id="cw-phone"
                      type="tel"
                      value={leadForm.phone}
                      onChange={(e) => updateLeadField("phone", e.target.value)}
                      aria-invalid={!!leadErrors.contact}
                      className="w-full rounded-lg border border-[var(--af-border)] bg-[var(--af-bg)] px-3 py-2 text-sm text-[var(--af-text)] outline-none focus:border-[var(--af-accent)]"
                    />
                  </div>
                  <div>
                    <label htmlFor="cw-email" className="mb-1 block text-[12px] font-medium text-[var(--af-text)]">
                      Имэйл
                    </label>
                    <input
                      id="cw-email"
                      type="email"
                      value={leadForm.email}
                      onChange={(e) => updateLeadField("email", e.target.value)}
                      aria-invalid={!!leadErrors.contact}
                      className="w-full rounded-lg border border-[var(--af-border)] bg-[var(--af-bg)] px-3 py-2 text-sm text-[var(--af-text)] outline-none focus:border-[var(--af-accent)]"
                    />
                  </div>
                </div>
                {leadErrors.contact && <p className="-mt-1 text-[11px] text-[#ef4444]">{leadErrors.contact}</p>}

                <div>
                  <label htmlFor="cw-company" className="mb-1 block text-[12px] font-medium text-[var(--af-text)]">
                    Байгууллага (заавал биш)
                  </label>
                  <input
                    id="cw-company"
                    type="text"
                    value={leadForm.company}
                    onChange={(e) => updateLeadField("company", e.target.value)}
                    className="w-full rounded-lg border border-[var(--af-border)] bg-[var(--af-bg)] px-3 py-2 text-sm text-[var(--af-text)] outline-none focus:border-[var(--af-accent)]"
                  />
                </div>

                <div>
                  <label className="flex items-start gap-2 text-[12px] text-[var(--af-text)]">
                    <input
                      type="checkbox"
                      checked={leadForm.consent}
                      onChange={(e) => updateLeadField("consent", e.target.checked)}
                      aria-invalid={!!leadErrors.consent}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-[var(--af-border)] accent-[var(--af-accent)]"
                    />
                    Холбоо барих мэдээллээ хадгалахыг зөвшөөрч байна
                  </label>
                  {leadErrors.consent && <p className="mt-1 text-[11px] text-[#ef4444]">{leadErrors.consent}</p>}
                </div>
              </div>

              <button
                type="submit"
                className="mt-4 flex w-full items-center justify-center rounded-lg bg-[var(--af-accent)] px-4 py-2.5 text-sm font-semibold text-black transition hover:bg-[var(--af-accent-hover)]"
              >
                Чатыг эхлүүлэх
              </button>
            </form>
          ) : (
            <>
              {/* Messages */}
              <div
                ref={scrollRef}
                role="log"
                aria-live="polite"
                aria-label="Чатны түүх"
                className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
              >
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                        m.role === "user"
                          ? "bg-[var(--af-accent)] text-black"
                          : "bg-[var(--af-bg-soft)] text-[var(--af-text)]"
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start" aria-label="Бичиж байна…">
                    <div className="rounded-2xl bg-[var(--af-bg-soft)] px-3.5 py-2.5 text-sm text-[var(--af-muted)]">
                      <span className="inline-flex gap-1">
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[var(--af-muted)] [animation-delay:-0.3s]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[var(--af-muted)] [animation-delay:-0.15s]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[var(--af-muted)]" />
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Composer */}
              <div className="flex items-center gap-2 border-t border-[var(--af-border)] p-3">
                <label htmlFor="af-chat-input" className="sr-only">
                  Мессеж бичих
                </label>
                <input
                  id="af-chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="agenticforce-ийн тухай асуугаарай…"
                  className="h-11 flex-1 rounded-lg border border-[var(--af-border)] bg-[var(--af-bg)] px-3 text-sm text-[var(--af-text)] placeholder:text-[var(--af-muted)] focus:border-[var(--af-accent)]"
                />
                <button
                  onClick={send}
                  disabled={loading || !input.trim()}
                  aria-label="Илгээх"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[var(--af-accent)] text-black transition hover:bg-[var(--af-accent-hover)] disabled:opacity-40"
                >
                  <Send size={16} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
